import React from 'react';

import { Typography } from 'antd-v5';
import type { ParagraphProps } from 'antd-v5/es/typography/Paragraph';
import type { TextProps as AntTextProps } from 'antd-v5/es/typography/Text';
import type { TitleProps } from 'antd-v5/es/typography/Title';
import cn from 'classnames';
import fonts from 'scouting-ui/lib/scss/_fonts.scss';
import type { ColorType } from 'scouting-ui/lib/types';

type FontVariants =
  | 'body-body-1'
  | 'body-body-2'
  | 'body-body-3'
  | 'body-caption'
  | 'body-caption'
  | 'display-h2'
  | 'display-h1'
  | 'display-h3'
  | 'display-h4'
  | 'body-overline'
  | 'buttons-large'
  | 'buttons-medium'
  | 'buttons-small';

interface CustomTextProps {
  font?: FontVariants;
  color?: ColorType;
}

export const Header: React.FC<TitleProps & CustomTextProps> = props => {
  const { font, color, ...otherProps } = props;
  const colorProp = color ? `var(--${color})` : undefined;
  const fontClass = font ? fonts[`text-${font}`] : undefined;
  return (
    <Typography.Title
      style={{ color: colorProp }}
      className={cn(fontClass)}
      {...otherProps}
    />
  );
};

export const Paragraph: React.FC<ParagraphProps & CustomTextProps> = props => {
  const { font = 'body-body-3', color, style, ...otherProps } = props;
  const fontClass = fonts ? fonts[`text-${font}`] : undefined;
  const colorProp = color ? `var(--${color})` : undefined;
  return (
    <Typography.Paragraph
      style={{ color: colorProp, ...style }}
      className={cn(fontClass, {})}
      {...otherProps}
    />
  );
};

export interface TextProps extends AntTextProps {
  font?: FontVariants;
  color?: ColorType;
  /** If true, uses Paragraph, otherwise inline. Default: `true` */
  paragraph?: boolean;
  /** If true, doesn't use paragraph as tag. Default: `false` */
  inline?: boolean;
}

export const Text: React.FC<TextProps> = props => {
  const {
    font = 'body-body-3',
    paragraph = true,
    inline = false,
    color,
    ...otherProps
  } = props;
  const fontClass = fonts ? fonts[`text-${font}`] : undefined;
  const colorProp = color ? `var(--${color})` : undefined;

  if (paragraph && !inline)
    return <Paragraph {...{ font, color }} {...otherProps} />;

  return (
    <Typography.Text
      style={{ color: colorProp }}
      className={cn(fontClass, {})}
      {...otherProps}
    />
  );
};

export default Text;
